import { useState, type FormEvent, type ReactNode } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { Btn, BtnInner, Confirm, Empty, ErrorNote, Facts, Field, Loading, Modal, Notice, PageHead, Section, ShownOnce } from "../components/kit";
import { api, get, identity, type Account, type AuditEntry, type Client, type ClientAccess, type ClientDetail, type ClientKind } from "../lib/api";
import { useAction, useResource } from "../lib/hooks";
import { AuditList } from "./Audit";
import { ClientBadges } from "./Clients";

interface FormState {
  id: string;
  name: string;
  kind: ClientKind;
  access: ClientAccess;
  public: boolean;
  redirects: string;
  logouts: string;
  notes: string;
}

const lines = (s: string) =>
  s
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean);

function fromClient(c: Client): FormState {
  return {
    id: c.id,
    name: c.name,
    kind: c.kind,
    access: c.access,
    public: c.public,
    redirects: c.redirect_uris.join("\n"),
    logouts: c.post_logout_redirect_uris.join("\n"),
    notes: c.notes,
  };
}

/** The fields of a portal, shared by registering and editing one. */
function ClientForm({
  initial,
  isNew,
  readOnly,
  submitLabel,
  onSubmit,
  error,
  busy,
}: {
  initial: FormState;
  isNew: boolean;
  readOnly?: boolean;
  submitLabel: string;
  onSubmit: (f: FormState) => void;
  error?: { message: string; fields: Record<string, string> } | null;
  busy: boolean;
}) {
  const [f, setF] = useState(initial);
  const set = <K extends keyof FormState>(k: K, v: FormState[K]) => setF((s) => ({ ...s, [k]: v }));
  const submit = (e: FormEvent) => {
    e.preventDefault();
    onSubmit(f);
  };
  return (
    <form className="form" onSubmit={submit}>
      <fieldset disabled={readOnly} className="form-grid">
        <Field label="Name" hint="Shown on the sign-in page: “to continue to …”." error={error?.fields.name}>
          <input className="input" value={f.name} onChange={(e) => set("name", e.target.value)} required maxLength={80} />
        </Field>
        <Field label="Client ID" hint={isNew ? "Lowercase letters, digits and hyphens. It cannot be changed later." : "Fixed when the portal was registered."} error={error?.fields.id}>
          <input
            className="input code"
            value={f.id}
            onChange={(e) => set("id", e.target.value.toLowerCase())}
            required
            pattern="[a-z][a-z0-9-]{1,62}"
            disabled={!isNew}
          />
        </Field>
        <Field label="Whose site">
          <Segmented
            value={f.kind}
            onChange={(v) => {
              set("kind", v);
              // A customer's site should not receive every account in the
              // house; start it on listed accounts.
              if (isNew) set("access", v === "customer" ? "listed" : "everyone");
            }}
            options={[
              ["group", "A Mashriqain site"],
              ["customer", "A customer's site"],
            ]}
          />
        </Field>
        <Field label="Who may sign in" hint={f.access === "listed" ? "Only accounts you add on the portal's page." : "Any enabled Mashriqain account."}>
          <Segmented
            value={f.access}
            onChange={(v) => set("access", v)}
            options={[
              ["everyone", "Every account"],
              ["listed", "Listed accounts"],
            ]}
          />
        </Field>
        {isNew && (
          <Field label="How it signs in" hint={f.public ? "A browser or mobile app with no server: it proves itself with PKCE and gets no secret." : "A site with a server that can keep a secret. PKCE is still recommended."}>
            <Segmented
              value={f.public ? "public" : "confidential"}
              onChange={(v) => set("public", v === "public")}
              options={[
                ["confidential", "Server (secret)"],
                ["public", "Browser app (PKCE)"],
              ]}
            />
          </Field>
        )}
        <Field label="Redirect URIs" hint="One per line, exactly as the portal sends them. HTTPS only (HTTP on localhost for development)." error={error?.fields.redirect_uris}>
          <textarea className="input code" rows={3} value={f.redirects} onChange={(e) => set("redirects", e.target.value)} required placeholder="https://portal.example.com/auth/callback" />
        </Field>
        <Field label="After sign-out" hint="Optional. Where the portal may send people after signing out, one per line.">
          <textarea className="input code" rows={2} value={f.logouts} onChange={(e) => set("logouts", e.target.value)} placeholder="https://portal.example.com/" />
        </Field>
        <Field label="Notes" hint="Only administrators see these: who owns it, who to call.">
          <textarea className="input" rows={2} value={f.notes} onChange={(e) => set("notes", e.target.value)} maxLength={1000} />
        </Field>
      </fieldset>
      {error && Object.keys(error.fields).length === 0 && <Notice tone="danger">{error.message}</Notice>}
      {!readOnly && (
        <div className="form-actions">
          <Btn variant="primary" type="submit" disabled={busy}>
            {busy ? "Saving…" : submitLabel}
          </Btn>
        </div>
      )}
    </form>
  );
}

export function Segmented<T extends string>({ value, onChange, options }: { value: T; onChange: (v: T) => void; options: [T, string][] }) {
  return (
    <span className="segmented" role="radiogroup">
      {options.map(([v, label]) => (
        <button key={v} type="button" role="radio" aria-checked={value === v} onClick={() => onChange(v)}>
          {label}
        </button>
      ))}
    </span>
  );
}

function body(f: FormState) {
  return {
    name: f.name,
    kind: f.kind,
    access: f.access,
    notes: f.notes,
    redirect_uris: lines(f.redirects),
    post_logout_redirect_uris: lines(f.logouts),
  };
}

/** What a portal's developer needs to connect it. */
function DeveloperSettings({ client, issuer, discovery, secret }: { client: Client; issuer: string; discovery: string; secret?: ReactNode }) {
  return (
    <Section title="Connection settings" aside={<span className="muted small">Give these to the portal's developer</span>}>
      <Facts
        items={[
          ["Issuer", <code key="i">{issuer}</code>],
          ["Discovery", <code key="d">{discovery}</code>],
          ["Client ID", <code key="c">{client.id}</code>],
          ["Client authentication", client.public ? "none (public client, PKCE with S256 required)" : "client_secret_basic or client_secret_post"],
          ["Scopes", <code key="s">openid profile email offline_access</code>],
        ]}
      />
      {secret}
    </Section>
  );
}

export function NewClientPage() {
  const navigate = useNavigate();
  const { busy, error, run } = useAction();
  const [made, setMade] = useState<{ client: Client; secret?: string } | null>(null);
  const settings = useResource<{ issuer: string; discovery_url: string }>("/settings");

  const create = async (f: FormState) => {
    const out = await run(() => api<{ client: Client; secret?: string }>("POST", "/clients", { id: f.id, public: f.public, ...body(f) }));
    if (out && !out.secret) navigate(`/portals/${out.client.id}`);
    else if (out) setMade(out);
  };

  if (made && settings.data) {
    return (
      <>
        <PageHead
          eyebrow="Portal registered"
          title={made.client.name}
          lede="It can now sign people in through Mashriqain ID. Give its developer the settings below."
        />
        <DeveloperSettings
          client={made.client}
          issuer={settings.data.issuer}
          discovery={settings.data.discovery_url}
          secret={made.secret && <ShownOnce label="Client secret" value={made.secret}>This is shown once. Store it in the portal's server configuration; if it is lost, make a new one from the portal's page.</ShownOnce>}
        />
        <div className="form-actions">
          <Link to={`/portals/${made.client.id}`} className="btn" data-variant="primary">
            <BtnInner arrow>Open the portal's page</BtnInner>
          </Link>
        </div>
      </>
    );
  }

  return (
    <>
      <PageHead
        title="Register a portal"
        lede="A site that will sign people in through Mashriqain ID. Its developer gets the connection settings once it is registered."
        back={
          <Link to="/portals" className="link back">
            ← Portals
          </Link>
        }
      />
      <section className="sheet">
        <ClientForm
          isNew
          initial={{ id: "", name: "", kind: "group", access: "everyone", public: false, redirects: "", logouts: "", notes: "" }}
          submitLabel="Register portal"
          onSubmit={create}
          error={error}
          busy={busy}
        />
      </section>
    </>
  );
}

export function ClientDetailPage() {
  const { id = "" } = useParams();
  const { data, error, reload } = useResource<ClientDetail>(`/clients/${id}`);
  const audit = useResource<{ entries: AuditEntry[] }>(`/audit?target_kind=client&target_id=${id}&limit=10`);
  const save = useAction();
  const action = useAction();
  const [secret, setSecret] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  if (error?.status === 404) {
    return (
      <>
        <PageHead
          eyebrow="Portals"
          title="No such portal."
          back={
            <Link to="/portals" className="link back">
              ← All portals
            </Link>
          }
        />
      </>
    );
  }
  if (!data) return error ? <ErrorNote error={error} /> : <Loading />;
  const c = data.client;
  const refresh = () => {
    reload();
    audit.reload();
  };

  return (
    <>
      <PageHead
        eyebrow="Portal"
        title={c.name}
        lede={<ClientBadges client={c} />}
        back={
          <Link to="/portals" className="link back">
            ← Portals
          </Link>
        }
      />
      {c.managed && <Notice title="This is the ops panel's own sign-in.">The server registers it on every start from OPS_URL, so it cannot be edited or disabled here.</Notice>}
      {c.disabled && <Notice tone="danger" title="This portal is disabled.">Nobody can sign in to it, and the sign-ins it had were ended. Enabling it lets people sign in again.</Notice>}
      <ErrorNote error={action.error} />

      <DeveloperSettings client={c} issuer={data.issuer} discovery={data.discovery_url} />

      {!c.managed && (
        <Section title="Manage">
          <div className="actions">
            {!c.public && (
              <Confirm
                label="Make a new secret"
                title="Replace the client secret?"
                body={<p>The current secret stops working immediately. The portal cannot sign anyone in until its configuration has the new one.</p>}
                confirm="Replace secret"
                onConfirm={async () => {
                  const out = await action.run(() => api<{ secret: string }>("POST", `/clients/${c.id}/secret`));
                  if (out) setSecret(out.secret);
                  refresh();
                }}
              />
            )}
            {c.disabled ? (
              <Confirm label="Enable portal" title="Enable this portal?" body={<p>People will be able to sign in to {c.name} again.</p>} confirm="Enable" variant="primary" onConfirm={() => action.run(() => api("POST", `/clients/${c.id}/enable`)).then(refresh)} />
            ) : (
              <Confirm
                label="Disable portal"
                title="Disable this portal?"
                body={<p>Nobody can sign in to {c.name} until it is enabled again, and every refresh token it holds is revoked. Its registration is kept.</p>}
                confirm="Disable portal"
                onConfirm={() => action.run(() => api("POST", `/clients/${c.id}/disable`)).then(refresh)}
              />
            )}
          </div>
        </Section>
      )}

      {(c.access === "listed" || data.accounts.length > 0) && <ListedAccounts client={c} accounts={data.accounts} onChange={refresh} />}

      <Section title="Settings" aside={saved && <span className="muted small">Saved.</span>}>
        <ClientForm
          key={c.updated_at}
          initial={fromClient(c)}
          isNew={false}
          readOnly={c.managed}
          submitLabel="Save changes"
          error={save.error}
          busy={save.busy}
          onSubmit={async (f) => {
            setSaved(false);
            const out = await save.run(() => api("PATCH", `/clients/${c.id}`, body(f)));
            if (out) {
              setSaved(true);
              refresh();
            }
          }}
        />
      </Section>

      <Section title="Changes by administrators">
        {audit.data ? <AuditList entries={audit.data.entries} /> : <Loading />}
      </Section>

      <Modal open={secret !== null} title="New client secret" onClose={() => setSecret(null)}>
        {secret && (
          <ShownOnce label={`Secret for ${c.id}`} value={secret}>
            This is shown once. Put it in the portal's server configuration now.
          </ShownOnce>
        )}
        <div className="modal-actions">
          <Btn variant="primary" onClick={() => setSecret(null)}>
            Done
          </Btn>
        </div>
      </Modal>
    </>
  );
}

/** The accounts allowed into a portal whose access is "listed". */
function ListedAccounts({ client, accounts, onChange }: { client: Client; accounts: ClientDetail["accounts"]; onChange: () => void }) {
  const [q, setQ] = useState("");
  const [found, setFound] = useState<Account[]>([]);
  const action = useAction();
  const listed = new Set(accounts.map((a) => a.id));

  const search = async (value: string) => {
    setQ(value);
    if (value.trim().length < 2) {
      setFound([]);
      return;
    }
    const page = await get<{ accounts: Account[] }>(`/accounts?q=${encodeURIComponent(value.trim())}`).catch(() => ({ accounts: [] }));
    setFound(page.accounts.filter((a) => !listed.has(a.id)).slice(0, 6));
  };
  const add = async (a: Account) => {
    await action.run(() => api("PUT", `/clients/${client.id}/accounts/${a.id}`));
    setQ("");
    setFound([]);
    onChange();
  };

  return (
    <Section title="Accounts that may sign in" aside={<span className="muted small">{accounts.length} listed</span>}>
      {client.access === "everyone" && <Notice tone="warn">Access is set to every account, so this list is not used right now.</Notice>}
      <ErrorNote error={action.error} />
      <div className="picker">
        <input className="input" type="search" placeholder="Add an account by name, email or phone" value={q} onChange={(e) => void search(e.target.value)} aria-label="Find an account to add" />
        {found.length > 0 && (
          <ul role="listbox" className="picker-list">
            {found.map((a) => (
              <li key={a.id}>
                <button type="button" onClick={() => void add(a)}>
                  <span className="row-title">{a.name}</span>
                  <span className="row-sub">{identity(a)}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
      {accounts.length === 0 ? (
        <Empty title="Nobody is listed yet, so nobody can sign in to this portal." />
      ) : (
        <ul role="list" className="rows">
          {accounts.map((a) => (
            <li key={a.id}>
              <Link to={`/accounts/${a.id}`} className="row-link">
                <span className="row-title">
                  {a.name}
                  {a.disabled && <span className="muted"> · disabled</span>}
                </span>
                <span className="row-sub">{identity(a)}</span>
              </Link>
              <Btn small onClick={() => action.run(() => api("DELETE", `/clients/${client.id}/accounts/${a.id}`)).then(onChange)}>
                Remove
              </Btn>
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}
